import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api, type PageResult } from '@/lib/api'

/**
 * API tokens (R7a) — Super Admin only, and only from a signed-in session: the
 * backend refuses every one of these routes to a request that itself carries a
 * token, so a leaked token cannot mint its own successor.
 *
 * A token acts AS its owner (a staff member) and today reaches `/i18n/*` only.
 * Its secret is in the create response and nowhere else — the server keeps a
 * SHA-256 of it — so the create dialog is the only place it is ever shown.
 */

export type ApiTokenStatus = 'active' | 'expired' | 'revoked'

export interface ApiToken {
  id: number
  name: string
  /** Non-secret head of the secret, e.g. `cwcat_AbC12x`. */
  token_prefix: string
  owner_public_id: string
  owner_name: string
  scope: 'i18n'
  status: ApiTokenStatus
  created_by: string
  /** Who minted it — not necessarily the owner, since the owner is selectable. */
  created_by_name: string
  created_at: string
  expires_at: string
  revoked_at: string | null
  revoked_by: string | null
  /** At most a minute stale — the server throttles the write. */
  last_used_at: string | null
}

export interface CreatedApiToken extends ApiToken {
  secret: string
}

export const MAX_TOKEN_DAYS = 365

export const createTokenSchema = z.object({
  name: z.string().trim().min(1, 'A name is required.').max(64, '64 characters at most.'),
  expires_in_days: z
    .number({ message: 'Enter a number of days.' })
    .int('Whole days only.')
    .min(1, 'At least one day.')
    .max(MAX_TOKEN_DAYS, `At most ${MAX_TOKEN_DAYS} days.`),
  /** Empty = the signed-in Super Admin. */
  owner_public_id: z.string().trim(),
})

export type CreateTokenInput = z.infer<typeof createTokenSchema>

const KEY = 'api-tokens'

/**
 * The newest 100 — the server's cap — paged in the browser. A handful of CI
 * tokens is the expected population; revoked ones are kept, so a screen that
 * one day needs more than 100 should switch DataTable to server paging.
 */
export function useApiTokens() {
  return useQuery({
    queryKey: [KEY],
    queryFn: () => api.get<PageResult<ApiToken>>('/admin/api-tokens?page=1&page_size=100'),
  })
}

export function useCreateApiToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTokenInput) =>
      api.post<CreatedApiToken>('/admin/api-tokens', {
        name: input.name,
        expires_in_days: input.expires_in_days,
        ...(input.owner_public_id ? { owner_public_id: input.owner_public_id } : {}),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useRevokeApiToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<ApiToken>(`/admin/api-tokens/${id}/revoke`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  })
}
